// src/composables/commen/useCrud.ts (Corrected)
import { ref, Ref } from 'vue';
import { toast, Content } from 'vue3-toastify'; // Import Content type
import apiService from '@/services/apiService'; // Adjust path as needed

// Define a generic type for your data items
interface CrudItem {
  _id?: string; // Assuming _id for identification
  [key: string]: any; // Allow other properties
}

export function useCrud<T extends CrudItem>(apiResource: string) {
  const data: Ref<T[]> = ref([]);
 const pagination = ref<any>({});
  const isLoading = ref(false);
  const error = ref<string | null>(null); // Keep internal error state as string | null

  const fetchData = async (params = { page: 1, limit: 10 }) => {
    isLoading.value = true;
    error.value = null;
    try {
      const resultArray = await apiService.get(apiResource, params); 
      if (Array.isArray(resultArray.data)) {
        data.value = [...resultArray.data]; 
        pagination.value = {...resultArray.pagination} ; 
      }
      else if(Array.isArray(resultArray)) {
         data.value = resultArray; 
      }
      else { 
        data.value = [];
        error.value = 'Received unexpected data format.';
        // Ensure message is a string before passing to toast
        toast.error(error.value as Content, { theme: "colored" });
      }
    } catch (err: any) { 
      data.value = [];
      // Provide a default message if error.value is null
      error.value = err.response?.data?.message || err.message || `Failed to load ${apiResource} data.`;
      // Ensure message is a string before passing to toast
      toast.error((error.value || 'An unknown error occurred while fetching data.') as Content, { theme: "colored" });
    } finally {
      isLoading.value = false;
    }
  };

  const createItem = async (newItem: Omit<T, '_id'>): Promise<T | null> => {
    isLoading.value = true;
    error.value = null;
    try {
      const createdItem: T = await apiService.post(apiResource, newItem);
      data.value.push(createdItem); // Update local list
      toast.success("Item created successfully" as Content, { theme: "colored" });
      return createdItem;
    } catch (err: any) { 
      error.value = err.response?.data?.message || err.message || "Failed to create item.";
      // Ensure message is a string before passing to toast
      toast.error((error.value || 'An unknown error occurred while creating the item.') as Content, { theme: "colored" });
      return null;
    } finally {
      isLoading.value = false;
    }
  };

  const updateItem = async (id: string, updatePayload: Partial<T>): Promise<T | null> => {
    isLoading.value = true;
    error.value = null;
    try {
      const updatedItem: T = await apiService.put(apiResource, id, updatePayload);
      const index = data.value.findIndex((item) => item._id === id);
      if (index !== -1) {
        data.value[index] = { ...data.value[index], ...updatedItem }; // Merge updates
      }
      toast.success("Item updated successfully" as Content, { theme: "colored" });
      return updatedItem;
    } catch (err: any) { 
      error.value = err.response?.data?.message || err.message || "Failed to update item.";
      // Ensure message is a string before passing to toast
      toast.error((error.value || 'An unknown error occurred while updating the item.') as Content, { theme: "colored" });
      return null;
    } finally {
      isLoading.value = false;
    }
  };

  const deleteItem = async (id: string): Promise<boolean> => {
    // Consider adding confirmation logic here or in the component calling this
    isLoading.value = true;
    error.value = null;
    try {
      await apiService.delete(apiResource, id);
      const index = data.value.findIndex((item) => item._id === id);
      if (index !== -1) {
        data.value.splice(index, 1); // Remove from local list
      }
      toast.success("Item deleted successfully" as Content, { theme: "colored" });
      return true;
    } catch (err: any) { 
      error.value = err.response?.data?.message || err.message || "Failed to delete item.";
      // Ensure message is a string before passing to toast
      toast.error((error.value || 'An unknown error occurred while deleting the item.') as Content, { theme: "colored" });
      return false;
    } finally {
      isLoading.value = false;
    }
  };

  return {
    data,
    pagination,
    isLoading,
    error, // Expose error state if needed by parent components
    fetchData,
    createItem,
    updateItem,
    deleteItem,
  };
}